import type { RunSummary } from '@shared/types/factory';
import { useLoader } from '@/hooks/use-loader';
import { listRuns } from '@/lib/runs-api';
import { isRunActive } from './run-status';

const POLL_MS = 5000;

/**
 * A task's runs, newest first (GET /api/runs?task_id=), polled every 5 s while one is still going.
 * One loader for the task detail: its Runs list and the timeline's "Factory · R-n" links read it.
 *
 * `newestMentioned` is the highest run number the task's timeline names: when a run starts without
 * the Run button (assigning the AI builder, T-2), its first event raises it and the list loads again.
 */
export function useTaskRuns(taskId: string, projectSlug: string | null, newestMentioned: number | null = null) {
  return useLoader(
    // Tagged with the task they're for: the last task's runs stay on screen while the next one's load (SF-T-209).
    async (signal) => ({ ...(await listRuns({ task_id: taskId, limit: 20 }, { signal, project: projectSlug ?? undefined })), taskId }),
    [taskId, projectSlug, newestMentioned],
    {
      enabled: Boolean(projectSlug),
      pollMs: POLL_MS,
      pollWhile: (res) => Boolean(res?.data?.runs.some((r) => isRunActive(r.status))),
    }
  );
}

/** The loaded runs by number, for the timeline's run links. */
export function runsByNumber(runs: RunSummary[]): Map<number, RunSummary> {
  return new Map(runs.map((r) => [r.number, r]));
}
